import { useTranslation } from 'react-i18next';
import {
  ImageIcon,
  Film,
  FileText,
  Rocket,
  Share2,
  Check,
  Filter,
  ChevronDown,
  SlidersHorizontal,
} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@scio3/ui';
import { cn } from '@scio3/ui';
import { FILE_TYPES } from '@scio3/shared-types';
import type { LucideIcon } from 'lucide-react';

/** Standard filter options for asset type dropdowns */
const TYPE_FILTER_OPTIONS: { value: string; labelKey: string; icon: LucideIcon }[] = [
  { value: FILE_TYPES.IMAGE, labelKey: 'Images', icon: ImageIcon },
  { value: FILE_TYPES.VIDEO, labelKey: 'Videos', icon: Film },
  { value: FILE_TYPES.DOCUMENT, labelKey: 'Documents', icon: FileText },
  { value: FILE_TYPES.WEB, labelKey: 'Apps', icon: Rocket },
];

export interface AssetTypeFilterDropdownProps {
  /** Current active filter value — null or undefined means "All Types" */
  value: string | null;
  /** Called when user selects a filter */
  onChange: (value: string | null) => void;
  /** Show "Shared with me" option */
  showShared?: boolean;
  /** Whether "Shared with me" is currently active */
  sharedActive?: boolean;
  /** Called when user selects "Shared with me" */
  onSharedChange?: (shared: boolean) => void;
  /**
   * Trigger button style:
   * - `"labeled"` — text label + chevron (used in SelectAssetModal)
   * - `"icon"` — compact icon-only button (used in Playlist asset picker)
   */
  trigger?: 'labeled' | 'icon';
  /** Dropdown alignment */
  align?: 'start' | 'end';
}

export function AssetTypeFilterDropdown({
  value,
  onChange,
  showShared,
  sharedActive,
  onSharedChange,
  trigger = 'labeled',
  align = 'start',
}: AssetTypeFilterDropdownProps) {
  const { t } = useTranslation();

  const hasActiveFilter = value != null || sharedActive;
  const activeLabel = sharedActive
    ? t('Shared with me')
    : value
      ? TYPE_FILTER_OPTIONS.find((o) => o.value === value)?.labelKey
      : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger === 'icon' ? (
          <Button
            variant={hasActiveFilter ? 'secondary' : 'outline'}
            size="icon-sm"
            className="shrink-0"
            aria-label={t('Filter by type')}
          >
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'shrink-0 gap-1.5 px-3',
              hasActiveFilter && 'border-primary/50 bg-primary/5 text-primary hover:bg-primary/10',
            )}
          >
            <Filter className="h-3.5 w-3.5" />
            {activeLabel ? t(activeLabel) : t('Type')}
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
          {t('Filter by')}
        </DropdownMenuLabel>

        {/* All Types */}
        <DropdownMenuItem
          onClick={() => {
            onChange(null);
            onSharedChange?.(false);
          }}
        >
          <span className="flex-1">{t('All')}</span>
          {!hasActiveFilter && <Check className="h-4 w-4" />}
        </DropdownMenuItem>

        {/* Type options */}
        {TYPE_FILTER_OPTIONS.map((opt) => {
          const isActive = value === opt.value && !sharedActive;
          return (
            <DropdownMenuItem
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                onSharedChange?.(false);
              }}
            >
              <opt.icon className="mr-2 h-4 w-4" />
              <span className="flex-1">{t(opt.labelKey)}</span>
              {isActive && <Check className="h-4 w-4" />}
            </DropdownMenuItem>
          );
        })}

        {/* Shared with me */}
        {showShared && (
          <DropdownMenuItem
            onClick={() => {
              onChange(null);
              onSharedChange?.(true);
            }}
          >
            <Share2 className="mr-2 h-4 w-4" />
            <span className="flex-1">{t('Shared with me')}</span>
            {sharedActive && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
